/**
 * Select — выбор одного значения из списка.
 *
 * Собран из того, что в системе уже есть: триггер — `Input` в режиме
 * `trigger` (он для того и заведён: «dropdown / picker / select triggers»),
 * список — всплывающее меню на токенах. Собственного хрома у молекулы нет,
 * поэтому поле выбора и поле ввода стоят в форме вровень: одна высота, один
 * радиус, одна граница, одни состояния.
 *
 * Теней в системе нет — слои разводятся поверхностью и границей
 * (`elevation.json`), поэтому список лежит на `surface2` с рамкой, а не на
 * приподнятой бумаге. Порядок наложения — `ZDropdown`, из той же таблицы.
 *
 * Доступность: триггер объявлен `combobox` с `aria-expanded`, список —
 * `listbox`, пункты — `option` с `aria-selected`. Клавиатура работает без
 * мыши: Enter, пробел и стрелка вниз открывают список, дальше стрелки, Home,
 * End и Esc обрабатывает само меню, а фокус после закрытия возвращается на
 * триггер.
 *
 * @format
 */

import {useId, useRef, useState, type KeyboardEvent} from 'react';

import {Box, Menu, MenuItem} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Input, type InputSize} from 'ui/components/atoms/Input';
import {spacingPx} from 'ui/components/layout/spacing';
import {DS_SCOPE_CLASS} from 'ui/theme/scope';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';
import {ZDropdown} from 'ui/tokens/ts/elevation';
import {RadiusM} from 'ui/tokens/ts/radii';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  /** Выбранное значение. Пусто — показывается `placeholder`. */
  value?: string;
  /** Новое значение. Без обработчика поле остаётся витриной. */
  onChange?: (value: string) => void;
  options: SelectOption[];
  /** Что стоит в поле, пока не выбрано. */
  placeholder?: string;
  /** Высота и типографика — те же ступени, что у `Input`. */
  size?: InputSize;
  fullWidth?: boolean;
  disabled?: boolean;
  /** Тон ошибки на триггере. */
  error?: boolean;
  /** Id триггера — для `FormField htmlFor`. */
  id?: string;
  /** Доступное имя, когда подписи рядом нет. */
  ariaLabel?: string;
  className?: string;
}

/**
 * Обёртка триггера — якорь для списка. Нужна ради ширины: список меряется по
 * ней, а не по внутреннему полю.
 */
const Anchor = styled(Box)({
  minWidth: 0,
});

/**
 * Список. Ширину берёт у триггера: список уже поля выглядит обрезком, шире —
 * вторым слоем поверх формы.
 */
const List = styled(Menu)({
  '& .MuiPaper-root': {
    backgroundColor: surfaceVar('surface2'),
    color: surfaceVar('onSurface'),
    border: `1px solid ${surfaceVar('surface3')}`,
    borderRadius: RadiusM,
    backgroundImage: 'none',
    boxShadow: 'none',
    marginTop: spacingPx('xs'),
  },
  '& .MuiList-root': {
    paddingTop: spacingPx('xs'),
    paddingBottom: spacingPx('xs'),
  },
});

const Option = styled(MenuItem)(({theme}) => ({
  ...text('body.l'),
  color: surfaceVar('onSurface'),
  paddingTop: spacingPx('s'),
  paddingBottom: spacingPx('s'),
  paddingLeft: spacingPx('m'),
  paddingRight: spacingPx('m'),
  whiteSpace: 'normal',
  '&:hover': {backgroundColor: surfaceVar('surface3')},
  // Выбранный пункт — заливка контейнера роли, а не галочка сбоку: список
  // короткий, и отметка читается быстрее значка.
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.container,
    color: theme.palette.primary.onContainer,
  },
  '&.Mui-selected:hover': {backgroundColor: theme.palette.primary.hoverContainer},
  '&.Mui-disabled': {color: theme.palette.disabledStates.high},
}));

/** Клавиши, которые открывают список с закрытого триггера. */
const OPEN_KEYS = ['Enter', ' ', 'ArrowDown', 'ArrowUp'];

export function Select({
  value,
  onChange,
  options,
  placeholder,
  size = 'm',
  fullWidth = false,
  disabled = false,
  error = false,
  id,
  ariaLabel,
  className,
}: SelectProps) {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const generatedId = useId();
  const listId = `${id ?? generatedId}-list`;

  const selected = options.find(option => option.value === value);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (disabled || !OPEN_KEYS.includes(event.key)) return;
    // Пробел и стрелки на readOnly-поле иначе прокрутят страницу.
    event.preventDefault();
    setOpen(true);
  };

  const handlePick = (option: SelectOption) => {
    setOpen(false);
    if (option.value !== value) onChange?.(option.value);
  };

  return (
    <Anchor ref={anchorRef} className={className}>
      <Input
        mode='trigger'
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
        error={error}
        id={id}
        value={selected?.label ?? ''}
        placeholder={placeholder}
        rightIcon={open ? 'expand_less' : 'expand_more'}
        onClick={() => !disabled && setOpen(true)}
        onKeyDown={handleKeyDown}
        role='combobox'
        aria-expanded={open}
        aria-haspopup='listbox'
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
      />

      <List
        id={listId}
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        style={{zIndex: ZDropdown}}
        PaperProps={{className: DS_SCOPE_CLASS, style: {width: anchorRef.current?.offsetWidth}}}
        MenuListProps={{role: 'listbox', 'aria-label': ariaLabel}}
      >
        {options.map(option => (
          <Option
            key={option.value}
            role='option'
            aria-selected={option.value === value}
            selected={option.value === value}
            disabled={option.disabled}
            onClick={() => handlePick(option)}
          >
            {option.label}
          </Option>
        ))}
      </List>
    </Anchor>
  );
}
